import { useReservationProductions } from './reservation-productions.hook'
import { AssignedProductionF } from '../../../../types/reservation-detail.types'
import { AssignedProductionBillPage } from 'src/app/pages/bills/utils'
import { useCallback, useMemo, useState } from 'react'
import { Typography, Stack, CircularProgress, Link } from '@vapor/material'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWarning } from '@fortawesome/pro-regular-svg-icons'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { ReservedResourceProductionDetailItemFragment } from 'src/api/api.generated'
import { RowRRProductionDetail } from './productions.types'
import { ProductionMovedDrawer } from './moved-productions-drawer'

type ProductionsCheckProps = {
  propertyId: string
  reservationId: string
  reservedResourceId?: string
  assignedProductions: (AssignedProductionF | AssignedProductionBillPage)[]
}
export const ProductionsCheck = (props: ProductionsCheckProps) => {
  const { dataProductions, loadingProductions, refetchProductionsDetail, loadingProductionsDetail } = useReservationProductions(props.propertyId, props.reservationId, props.reservedResourceId)
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const [details, setDetails] = useState<RowRRProductionDetail[]>()
  const [showDetails, setShowDetails] = useState<boolean>(false)

  const assignedProductionsIds = useMemo(() => {
    return props.assignedProductions.map((ap) => ap.production.id)
  }, [props.assignedProductions])

  const check = useMemo(() => {
    let result = true
    if (dataProductions?.reservedResources) {
      result = !dataProductions.reservedResources.some((rr) => {
        const result2 = rr?.productions?.some((p) => !assignedProductionsIds.includes(p.id))
        return result2
      })
    }
    return result
  }, [dataProductions, assignedProductionsIds])

  const getDetails = useCallback(async () => {
    try {
      const response = await refetchProductionsDetail({
        filter: {
          tenantId: { eq: tenantId },
          propertyId: { eq: props.propertyId },
          id: props.reservedResourceId ? { eq: props.reservedResourceId } : undefined,
          reservationId: !props.reservedResourceId ? { eq: props.reservationId } : undefined,
        },
      })
      if (response.data.reservedResources) {
        const flatProductions: RowRRProductionDetail[] | undefined = flaternReservedResourceProductions(response.data.reservedResources)

        if (flatProductions && dataProductions?.reservedResources) {
          const result = dataProductions.reservedResources?.reduce((accRR: string[], currRR) => {
            const temp =
              currRR.productions?.reduce((accProd: string[], currProd) => {
                if (!assignedProductionsIds.includes(currProd.id)) return [...accProd, currProd.id]
                else return accProd
              }, []) || []

            return [...accRR, ...temp]
          }, [])
          setDetails(flatProductions.filter((fp) => result.includes(fp.id)))
          setShowDetails(true)
        }
      }
    } catch (e) {
      console.log('Something went wrong!', e)
    }
  }, [tenantId, props.propertyId, dataProductions, props.reservationId, props.reservedResourceId, assignedProductionsIds, details, refetchProductionsDetail])

  return (
    <>
      {(loadingProductions || loadingProductionsDetail) && <CircularProgress size={14} />}
      <>
        {check ? (
          ''
        ) : (
          <Stack direction="row" spacing={1}>
            <FontAwesomeIcon icon={faWarning} color={'orange'} />
            <Typography variant="body">
              <Link onClick={() => getDetails()}>{t('pms.booking.reservation.production_check')}</Link>
            </Typography>
          </Stack>
        )}
      </>
      <ProductionMovedDrawer //
        propertyId={props.propertyId}
        rows={details ?? []}
        open={showDetails}
        onClose={() => setShowDetails(false)}
      />
    </>
  )
}

type BillDestinationReference = {
  billId: string
  billDescription: string
  reservationId?: string
  reservationCode?: string
  quota: number
}

const flaternReservedResourceProductions = (args: ReservedResourceProductionDetailItemFragment[]): RowRRProductionDetail[] | undefined => {
  return args.reduce((accRR: RowRRProductionDetail[], currRR) => {
    const resultTemp =
      currRR.productions?.reduce((acc: RowRRProductionDetail[], curr) => {
        const result = curr.bills?.map<BillDestinationReference>((b) => ({
          billId: b.bill.id,
          billDescription: b.bill.description,
          reservationId: b.bill.reservation?.[0]?.id,
          reservationCode: b.bill.reservation?.[0]?.reservationCode,
          quota: b.quota,
        }))
        const resultFlat: RowRRProductionDetail[] = []

        result?.forEach((r) => {
          resultFlat.push({
            id: curr.id,
            date: curr.date,
            description: curr.description,
            quantity: curr.quantity,
            amount: curr.amount,
            total: curr.total,
            currency: curr.currency,
            chargeCategoryName: curr.chargeCategory.name,
            quota: r.quota,
            billDescription: r.billDescription,
            reservationCode: r.reservationCode,
            reservationId: r.reservationId,
            billId: r.billId,
          })
        })

        return [...acc, ...resultFlat]
      }, []) ?? []

    return [...accRR, ...resultTemp]
  }, [])
}
